<script setup lang="ts">
import { z } from 'zod'
import type { FormSubmitEvent } from '@nuxt/ui'

const toast = useToast()

const { $pb } = useNuxtApp()

const { user } = useAuth()

const props = defineProps<{
  email?: string
}>()

async function requestVerification(email: string) {
  try {
    requestingVerification.value = true
    await $pb.collection('users').requestVerification(email)
    toast.add({
      title: 'Verification Email Sent',
      description: 'We\'ve sent a verification email to your inbox. Please check your email and click the link to verify your account.',
      icon: 'i-heroicons-check-circle',
      color: 'success'
    })
  } catch (error: any) {
    toast.add({
      title: 'Verification Email Failed',
      description: error.message,
      icon: 'i-heroicons-exclamation-triangle',
      color: 'error'
    })
  } finally {
    requestingVerification.value = false
  }
}

const emailForVerification = reactive({
  email: props.email
})

const emailSchema = z.object({
  email: z.string().email()
})

type EmailSchema = z.infer<typeof emailSchema>

const requestingVerification = ref(false)
async function doRequestVerification(event: FormSubmitEvent<EmailSchema>) {
  try {
    await requestVerification(event.data.email)
  } catch (error: any) {
    toast.add({
      title: 'Verification Email Failed',
      description: error.message,
      icon: 'i-heroicons-exclamation-triangle',
      color: 'error'
    })
  }
}

const emailForm = useTemplateRef('emailForm')
async function triggerRequestVerification() {
  emailForVerification.email = user.value?.email as string
  await emailForm.value?.submit()
}

</script>

<template>
  <div>
    <h1 class="text-2xl font-bold">Verify your email</h1>
    <p class="text-sm py-2">
      We've sent a verification email to your inbox for <strong>{{ user?.email || 'your email' }}</strong>.
      Please check your email and click the link to verify your account.
    </p>

    <div v-if="user?.email">
      <p class="text-sm py-2">
        If you don't see the email, please check your spam folder or
        <a
          href="javascript:void(0)"
          class="text-(--ui-primary) font-medium"
          @click="triggerRequestVerification"
        >
          request a new verification email
        </a>.
      </p>
    </div>
    <div v-show="!user?.email">
      <p class="text-sm py-2">
        If you don't see the email, please check your spam folder or request a new verification email.
      </p>
      <UForm
        ref="emailForm"
        class="w-full max-w-sm space-y-6"
        :state="emailForVerification"
        :schema="emailSchema"
        :validate-on="['input', 'blur']"
        @submit="doRequestVerification"
      >
        <UFormField label="Email" required>
          <UInput
            v-model="emailForVerification.email"
            placeholder="Enter your email"
            class="w-full"
          />
        </UFormField>
        <UButton
          type="submit"
          :loading="requestingVerification"
          :disabled="requestingVerification"
          class="w-full mt-4"
          block
        >
          Request Verification
        </UButton>
      </UForm>
    </div>
  </div>
</template>